import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '@/lib/formatPrice';
import { recommendedPrograms } from '@/mocks/corporateRetailData';

function renderParagraphs(text: string, className: string) {
  return text.split(/(?:\r?\n){2,}/).map((paragraph, index) => (
    <p key={index} className={className}>
      {paragraph}
    </p>
  ));
}

export default function RecommendedPrograms() {
  const { t } = useTranslation();

  return (
    <section className="relative py-20 md:py-28 bg-background-50">
      <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16">
        <div className="text-center mb-14">
          {renderParagraphs(
            String(t('corporateRetail.rec.label')),
            'text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label',
          )}
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
            {t('corporateRetail.rec.title')}
          </h2>
          {renderParagraphs(
            String(t('corporateRetail.rec.titleEn')),
            'section-title-en text-accent-500 text-[15px] tracking-[0.12em] mb-4',
          )}
          <div className="gold-line mx-auto mb-5"></div>
          {renderParagraphs(
            String(t('corporateRetail.rec.lead')),
            'text-[16px] text-foreground-600 tracking-[0.04em] leading-relaxed max-w-[650px] mx-auto',
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {recommendedPrograms.map((program, idx) => (
            <div
              key={idx}
              className={`card-glow bg-background-100/60 border p-5 flex flex-col ${
                program.recommended
                  ? 'border-accent-500/40 bg-accent-500/3'
                  : 'border-background-200/60'
              }`}
            >
              <div className="flex items-center gap-2 mb-3">
                <h3 className="font-heading text-[17px] text-foreground-900 tracking-[0.06em]">
                  {t(`corporateRetail.pricing.plan${idx}.name`)}
                </h3>
                {program.recommended && (
                  <span className="text-[12px] bg-accent-500/80 text-primary-900 px-2 py-0.5 tracking-[0.08em] font-bold">
                    {t('corporateRetail.rec.badge')}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 mb-3 text-[14px]">
                <span className="text-foreground-500 tracking-[0.04em]">{t(`corporateRetail.rec.${idx}.duration`)}</span>
                <span className="text-accent-600 font-medium tracking-[0.04em]">{formatPrice(program.price)}{t('corporateRetail.rec.priceSuffix')}</span>
              </div>
              {renderParagraphs(
                String(t(`corporateRetail.rec.${idx}.modules`)),
                'text-[13px] text-foreground-400 tracking-[0.04em] mb-3 leading-relaxed',
              )}
              {renderParagraphs(
                String(t(`corporateRetail.rec.${idx}.description`)),
                'text-[15px] text-foreground-600 tracking-[0.04em] leading-relaxed mb-4 flex-1',
              )}
            </div>
          ))}
        </div>

        <p className="text-center mt-8 text-[14px] text-foreground-400 tracking-[0.04em]">
          {t('corporateRetail.rec.corporateLinkText')}{' '}
          <Link to="/corporate" className="text-accent-600 hover:text-accent-500 transition-colors">
            {t('corporateRetail.rec.corporateLink')}
          </Link>
          {' '}{t('corporateRetail.rec.corporateLinkSuffix')}
        </p>
      </div>
    </section>
  );
}
